import { resolveId } from '@medplum/core'
import type { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import type { SeenMedplumClient } from '@seen/core/client'
import { getDateTimePeriodDisplay } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import type * as appointments from '@seen/core/models/appointments/index'
import { AppointmentEncounter } from '@seen/core/models/encounters/appointment-encounter'
import { createAppointmentEncounterIfNoneExist } from '@seen/core/models/encounters/transactions'
import { Order } from '@seen/core/models/orders/Order'
import { Button, cn } from '@seen/ui'
import { IconClock, IconDoorEnter, IconDoorExit } from '@tabler/icons-react'
import { formatDistance } from 'date-fns'
import { toast } from 'sonner'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function AppointmentEncounterCheckInOut({
  appointment,
  compact = false,
  hideDuration = false,
  hideText = false,
  hideIcon = false,
  className,
  colored = false,
  onCheckedIn,
  onCheckedOut,
}: {
  appointment: appointments.ExtendedAppointment
  compact?: boolean
  hideDuration?: boolean
  hideText?: boolean
  hideIcon?: boolean
  className?: string
  colored?: boolean
  onCheckedIn?: () => void
  onCheckedOut?: () => void
}): React.ReactNode {
  const medplum = useMedplum()
  const encounterQuery = useSearchOne(
    AppointmentEncounter,
    AppointmentEncounter.searchByAppointment(appointment.reference)
  )

  const transactionMutation = useTransactionMutation()

  const patchEncounterMutation = usePatchMutation<AppointmentEncounter>()

  if (appointment.isCancelled) {
    return null
  }

  if (encounterQuery.isLoading) {
    return <Loader />
  }

  const encounter = encounterQuery.data

  if (!encounter && !appointment.shouldShowCheckIn) {
    return null
  }

  if (!encounter) {
    let text = 'Check In'
    if (appointment.isSpecialistVisit) {
      text = 'Complete'
    }
    return (
      <Button
        className={cn(
          'flex w-full items-center justify-center gap-1',
          compact && 'w-fit p-1 text-xs',
          className,
          colored && 'bg-green-500 text-white'
        )}
        variant="outline"
        size={compact ? 'icon' : 'default'}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          transactionMutation.mutate(
            async (transaction) => {
              await handleCheckIn({ medplum, appointment, transaction })
            },
            {
              onSuccess: () => {
                void encounterQuery.refetch()
                onCheckedIn?.()
                toast.dismiss(`start-encounter-${appointment.id}`)
              },
            }
          )
        }}
        loading={transactionMutation.isPending}
      >
        {!hideIcon && <IconDoorEnter className="text-green-500" />}
        {hideText ? null : text}
      </Button>
    )
  }

  if (encounter.endTime && encounter.startTime) {
    if (hideDuration) {
      return null
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <p className={cn(className, 'flex items-center gap-x-1 text-sm')}>
            <IconClock className="size-3" />
            {formatDistance(encounter.endTime, encounter.startTime)}
          </p>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {getDateTimePeriodDisplay({
              start: encounter.startTime,
              end: encounter.endTime,
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button
      className={cn(
        'flex w-full items-center justify-center gap-2',
        compact && 'w-fit p-1 text-xs',
        className,
        colored && 'bg-red-500 text-white'
      )}
      variant="outline"
      size={compact ? 'icon' : 'default'}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        patchEncounterMutation.mutate(
          {
            instance: encounter,
            mutateFn: (e) => {
              e.finish()
            },
          },
          {
            onSuccess: () => {
              onCheckedOut?.()
            },
          }
        )
      }}
      loading={patchEncounterMutation.isPending}
    >
      {!hideIcon && <IconDoorExit className="text-red-500" />}
      {hideText ? null : 'Check Out'}
    </Button>
  )
}

async function handleCheckIn({
  medplum,
  appointment,
  transaction,
}: {
  medplum: SeenMedplumClient
  appointment: appointments.ExtendedAppointment
  transaction: TransactionMutationBuilder
}): Promise<void> {
  const patient = appointment?.patient
  invariant(patient, 'Patient is required')

  const encounter = new AppointmentEncounter({
    appointment: appointment.reference,
    patient,
    practitioner: appointment?.practitioners[0],
    status: 'in-progress',
  })
  if (appointment.isSpecialistVisit) {
    encounter.start(appointment.start)
    encounter.finish(appointment.end)
  } else {
    encounter.start()
  }

  const basedOnRefs = appointment.basedOn ?? []
  if (basedOnRefs.length > 0) {
    const orders = await medplum.searchResourceClassPagesAll(Order, {
      _id: basedOnRefs.map(resolveId),
    })
    for (const order of orders) {
      if (order.status === 'coordinated') {
        transaction.patch(order, (o) => {
          o.effectuate()
        })
      }
    }
  }

  createAppointmentEncounterIfNoneExist(transaction, encounter)
}
